import React, { useRef, useState, useCallback } from "react";

// Virtual joystick — outputs a normalized (dx, dz) vector in the game's
// world-XZ space into the supplied inputRef. The component only sets refs
// and a tiny piece of local React state for the knob transform, so it does
// not trigger any re-renders in the game loop.
export default function Joystick({ inputRef, side = "left" }) {
  const baseRef = useRef(null);
  const baseRectRef = useRef(null);
  const activePointerId = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0, active: false });

  const updateFromClient = useCallback(
    (cx, cy) => {
      const rect = baseRectRef.current;
      if (!rect) return;
      const r = rect.width / 2;
      const ox = cx - (rect.left + r);
      const oy = cy - (rect.top + r);
      const mag = Math.hypot(ox, oy);
      const clamped = Math.min(mag, r);
      const nx = mag > 0 ? (ox / mag) * clamped : 0;
      const ny = mag > 0 ? (oy / mag) * clamped : 0;
      setKnob({ x: nx, y: ny, active: true });
      const dx = nx / r;
      const dy = ny / r;
      // Apply a small dead-zone so a resting thumb doesn't drift the UFO.
      const dead = 0.12;
      const dmag = Math.hypot(dx, dy);
      let outX = 0;
      let outZ = 0;
      if (dmag > dead) {
        const scale = (dmag - dead) / (1 - dead);
        outX = (dx / dmag) * scale;
        outZ = (dy / dmag) * scale;
      }
      inputRef.current.joystick = { dx: outX, dz: outZ, active: true };
    },
    [inputRef]
  );

  const onDown = useCallback(
    (e) => {
      e.preventDefault();
      activePointerId.current = e.pointerId;
      baseRectRef.current = baseRef.current.getBoundingClientRect();
      try {
        baseRef.current.setPointerCapture(e.pointerId);
      } catch (_) {}
      updateFromClient(e.clientX, e.clientY);
    },
    [updateFromClient]
  );

  const onMove = useCallback(
    (e) => {
      if (e.pointerId !== activePointerId.current) return;
      e.preventDefault();
      updateFromClient(e.clientX, e.clientY);
    },
    [updateFromClient]
  );

  const onEnd = useCallback(
    (e) => {
      if (e.pointerId !== activePointerId.current) return;
      e.preventDefault();
      activePointerId.current = null;
      setKnob({ x: 0, y: 0, active: false });
      inputRef.current.joystick = { dx: 0, dz: 0, active: false };
    },
    [inputRef]
  );

  return (
    <div
      className={`joystick joystick-${side}`}
      data-testid="joystick"
      data-active={knob.active ? "true" : "false"}
    >
      <div
        ref={baseRef}
        className="joystick-base"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onEnd}
        onPointerCancel={onEnd}
        data-testid="joystick-base"
      >
        <div className="joystick-rim" />
        <div
          className="joystick-knob"
          style={{
            transform: `translate(-50%, -50%) translate(${knob.x}px, ${knob.y}px)`,
            transition: knob.active ? "none" : "transform 0.18s ease-out",
          }}
          data-testid="joystick-knob"
        />
      </div>
    </div>
  );
}
